import _ from 'lodash'
import moment from 'moment'
import { date, Dialog, Platform } from 'quasar'
import { Capacitor } from '@capacitor/core'
import { Share } from '@capacitor/share'
// Import all filter functions
import * as filters from './filters.js'
import * as locations from './locations.js'
import * as routines from './routines.js'
import * as eventWindow from './eventWindow.js'
import { isNativeUri, resolveSavedImage, toOriginalImageUrl } from './image.js'
import { fetchBlobSafe, addFilesToZip } from './fetchBlobSafe'
import { organizationIdOf } from './user'
import { normalizeAddonImageUrl } from './addonImageUrl'
import { FULL_QUALITY_REENCODE, getPhotoQualityProfile } from '@/utils/photoQuality'
import * as answerOptions from './answerOptions.js'
import * as logicRules from './logicRules.js'
import * as tasks from './tasks.js'
import * as visibilityGraph from './visibilityGraph.js'
import * as analytics from './analytics.js'
import { buildCommentsToResync } from './addons.js'
import { isItemVisibleByLogic, isAnswerGiven } from './formVisibility.js'
import {
  getCemexScoreResult,
  getItemObtainedPoints,
  getItemPossiblePoints,
  getMifelScoreResult,
  sumArray,
  sumKey,
  sumKeyWhereNotEqual
} from './scoring.js'
import { isDirectGeneralPdfAllowed } from './reportSizing.js'
import { saveFileToDevice } from './deviceFile.js'

export { PICTURE_ADDON_TYPES, countOccupiedPictureSlots } from './pictureAddonTypes.js'
// Re-export nombrado: los componentes importan siempre desde este barrel (ver CLAUDE.md).
export { dedupeRoutineExecutions } from './routines.js'

// ---------- UNIVERSAL IMAGE COMPRESSOR ----------
// ⚠️ NO "ARREGLES" LOS CALL-SITES QUE LLAMAN (x, 0.5, 300) — parecen tener los args invertidos y LOS
// TIENEN, pero hoy son IDENTIDAD POR ACCIDENTE y eso es lo único que evita una pérdida de evidencia:
//   maxWidth=0.5 -> canvas.width = w*(0.5/w) = 0.5 -> WebIDL `unsigned long` TRUNCA a 0 -> canvas 0x0
//   -> toBlob() devuelve null -> el `if (!blob) return resolve(inputBase64)` de abajo -> devuelve la
//   ENTRADA SIN TOCAR. (Verificado empíricamente en navegador, no deducido.)
// Invertirlos a (x, 300, 0.5) haría que `thumbnail` fuera un JPEG real de 300x225/~15KB, y esos
// call-sites hacen `addon.answer = thumb` (OccurrenceItemAddonDialog.vue, OccurrenceItemAddonsSection.vue) -> con el flag offline ON,
// addImage -> enqueueAddonV2({ photoBase64: answer }) SUBIRÍA ese thumb de 300px al backend como si
// fuera la foto. Pérdida de evidencia IRREVERSIBLE en producción.
// Si algún día se quiere un thumbnail de verdad: hay que borrar el `answer = thumbnail` EN EL MISMO
// COMMIT, no antes.
export function compressBase64 (inputBase64, maxWidth = 1500, quality = 0.85) {
  return new Promise(resolve => {
    if (!inputBase64 || typeof inputBase64 !== 'string') {
      console.warn('[compressBase64] base64 inválido')
      return resolve(null)
    }

    const img = new Image()
    img.src = inputBase64

    img.onload = () => {
      if (!img.width || !img.height) {
        console.warn('[compressBase64] imagen sin dimensiones')
        return resolve(inputBase64)
      }

      const canvas = document.createElement('canvas')
      const scale = img.width > maxWidth ? maxWidth / img.width : 1

      canvas.width = img.width * scale
      canvas.height = img.height * scale

      const ctx = canvas.getContext('2d')
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height)

      if (!canvas.toBlob) {
        return resolve(inputBase64)
      }

      canvas.toBlob(blob => {
        if (!blob) return resolve(inputBase64)

        const reader = new FileReader()
        reader.onloadend = () => {
          resolve(typeof reader.result === 'string' ? reader.result : inputBase64)
        }
        reader.readAsDataURL(blob)
      }, 'image/jpeg', quality)
      canvas.width = canvas.height = 0
    }

    img.onerror = () => {
      console.warn('[compressBase64] error al cargar imagen')
      resolve(inputBase64)
    }
  })
}

// Normaliza File | Blob | string (base64 / data URL / URL) a Blob.
async function imageSourceToBlob (source, label) {
  if (!source) throw new TypeError(`[${label}] fuente de imagen requerida`)

  if (source instanceof File || source instanceof Blob) return source

  if (typeof source === 'string') {
    const res = await fetch(source)
    if (!res.ok && !source.startsWith('data:')) {
      throw new Error(`[${label}] No se pudo cargar la imagen (status ${res.status})`)
    }
    return res.blob()
  }

  throw new TypeError(`[${label}] Tipo de fuente no soportado`)
}

// ---------- BLOB-BASED IMAGE COMPRESSOR (reemplaza compressBase64) ----------
// Acepta File, Blob o string (base64 / data URL). Devuelve { blob, url }.
// El caller debe llamar URL.revokeObjectURL(url) cuando ya no necesite el preview.
// Lanza Error en caso de fallo — el caller debe envolver en try/catch.
export async function compressImage (source, maxWidth = 1500, quality = 0.85) {
  // --- 1. Normalizar fuente a Blob ---
  const inputBlob = await imageSourceToBlob(source, 'compressImage')

  // --- 2. Ruta principal: createImageBitmap (Android + iOS 15+) ---
  // Envuelto en try-catch: en algunos dispositivos (ej. OPPO/Realme con imágenes
  // grandes) createImageBitmap está definido pero lanza TypeError al ejecutarse.
  // En ese caso caemos al fallback de <img> + canvas.
  if (typeof createImageBitmap !== 'undefined') {
    try {
      const bitmap = await createImageBitmap(inputBlob)
      let blob

      try {
        const scale = bitmap.width > maxWidth ? maxWidth / bitmap.width : 1
        const width = Math.round(bitmap.width * scale)
        const height = Math.round(bitmap.height * scale)

        if (typeof OffscreenCanvas !== 'undefined') {
          // Android WebView 69+ / iOS 16.4+ — off main thread
          const canvas = new OffscreenCanvas(width, height)
          canvas.getContext('2d').drawImage(bitmap, 0, 0, width, height)
          blob = await canvas.convertToBlob({ type: 'image/jpeg', quality })
          canvas.width = canvas.height = 0
        } else {
          // iOS 15 / Safari sin OffscreenCanvas — main thread pero sin FileReader
          const canvas = document.createElement('canvas')
          canvas.width = width
          canvas.height = height
          canvas.getContext('2d').drawImage(bitmap, 0, 0, width, height)
          blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', quality))
          canvas.width = canvas.height = 0
        }
      } finally {
        bitmap.close()
      }

      if (!blob) throw new Error('[compressImage] canvas no produjo blob (ruta createImageBitmap)')
      return { blob, url: URL.createObjectURL(blob) }
    } catch (err) {
      console.warn('[compressImage] createImageBitmap falló, usando fallback img+canvas:', err.message)
    }
  }

  // --- 3. Fallback iOS 13/14: sin createImageBitmap ---
  const tempUrl = URL.createObjectURL(inputBlob)
  const img = new Image()

  await new Promise((resolve, reject) => {
    img.onload = resolve
    img.onerror = () => {
      URL.revokeObjectURL(tempUrl)
      reject(new Error('[compressImage] Error al cargar imagen (fallback iOS 13/14)'))
    }
    img.src = tempUrl
  })

  URL.revokeObjectURL(tempUrl)

  if (!img.width || !img.height) {
    throw new Error('[compressImage] Imagen sin dimensiones (fallback iOS 13/14)')
  }

  const scale = img.width > maxWidth ? maxWidth / img.width : 1
  const canvas = document.createElement('canvas')
  canvas.width = Math.round(img.width * scale)
  canvas.height = Math.round(img.height * scale)
  canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height)

  const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', quality))
  canvas.width = canvas.height = 0
  if (!blob) throw new Error('[compressImage] canvas.toBlob devolvió null (fallback iOS 13/14)')

  return { blob, url: URL.createObjectURL(blob) }
}

// ---------- PERFIL DE CALIDAD POR ORGANIZACIÓN ----------
// Ancho tan alto que `compressImage` nunca reescala (solo reduce cuando la imagen lo supera).
const PASSTHROUGH_MAX_WIDTH = 100000
const PASSTHROUGH_QUALITY = FULL_QUALITY_REENCODE

/**
 * Entrega el binario de entrada SIN pasar por canvas cuando ya es JPEG. El formato se detecta por
 * firma de bytes y no por `type`: readImageAsBlob puede devolver el blob sin mime. Lo que no es JPEG
 * se normaliza a JPEG respetando el techo recibido, para que el archivo subido siga coincidiendo
 * con su extensión `.jpg` sin exceder el perfil de calidad.
 */
export async function passthroughImage (source, maxWidth = PASSTHROUGH_MAX_WIDTH) {
  const inputBlob = await imageSourceToBlob(source, 'passthroughImage')
  const head = new Uint8Array(await inputBlob.slice(0, 3).arrayBuffer())

  if (!(head[0] === 0xFF && head[1] === 0xD8 && head[2] === 0xFF)) {
    return compressImage(inputBlob, maxWidth, PASSTHROUGH_QUALITY)
  }

  const blob = inputBlob.type === 'image/jpeg' ? inputBlob : new Blob([inputBlob], { type: 'image/jpeg' })

  return { blob, url: URL.createObjectURL(blob) }
}

/**
 * Punto único de procesado de las fotos de ocurrencias. `maxWidth`/`quality` son los valores del
 * comportamiento vigente y solo se aplican cuando el perfil de la organización no pide passthrough.
 */
export async function processPhoto (source, {
  maxWidth = 1500,
  quality = 0.85,
  profile,
  organizationId
} = {}) {
  const active = profile || getPhotoQualityProfile({ organizationId })

  return active.passthrough
    ? passthroughImage(source, active.targetWidth ?? PASSTHROUGH_MAX_WIDTH)
    : compressImage(source, maxWidth, quality)
}

// ---------- TKT-1976: REPARA VALUES DUPLICADOS EN OPCIONES SELECT ----------
// El builder viejo asignaba a cada opción un value igual a su posición, pero al
// borrar una opción las posiciones se recorren y los values sobrevivientes no se
// reindexan: una opción nueva podía recibir un value YA OCUPADO. El q-select del
// llenado usa emit-value + map-options, así que con values repetidos todas las
// opciones que los comparten se comportan como una sola (solo se puede elegir la
// primera y muestra su etiqueta).
//
// Política de reparación — DEBE ser idéntica al repairer del backend
// (App\Services\Forms\FormTableOptionValueRepairer): la primera opción que usa un
// value lo CONSERVA; cada duplicada recibe un value nuevo por encima del máximo de
// la columna (max+1, +2, …) y NUNCA rellena los huecos que dejaron los borrados.
// Reindexar o rellenar huecos cambiaría el significado de las respuestas ya
// guardadas, y si el value que se guarda al llenar no coincide con el que el
// backend asigna al reparar la definición del form, ese valor quedaría huérfano en
// reportes/PDF. Idempotente: si los values ya son únicos, devuelve el arreglo original.
export function uniqueSelectOptionValues (options) {
  if (!Array.isArray(options) || options.length < 2) {
    return options
  }

  let nextValue = maxNumericOptionValue(options)
  const seen = new Set()
  let changed = false

  const repaired = options.map((option) => {
    const key = Object.prototype.hasOwnProperty.call(option, 'value') ? String(option.value) : ''

    if (!seen.has(key)) {
      seen.add(key)
      return option
    }

    // Duplicado: value nuevo por encima de todos los usados en la columna,
    // incluidos los que ya se reasignaron aquí.
    nextValue = nextValue === null ? 0 : nextValue + 1
    seen.add(String(nextValue))
    changed = true
    return { ...option, value: nextValue }
  })

  return changed ? repaired : options
}

// Value numérico más alto de la columna, o null si ninguno lo es. Trata igual que
// el is_numeric del backend: el value ' ' (placeholder del builder viejo) NO cuenta.
export function maxNumericOptionValue (options) {
  let max = null

  options.forEach((option) => {
    const raw = option.value
    if (raw === null || raw === undefined || String(raw).trim() === '' || !Number.isFinite(Number(raw))) {
      return
    }
    const value = Number.parseInt(raw, 10)
    max = max === null ? value : Math.max(max, value)
  })

  return max
}

const utils = {
  ...filters,
  ...locations,
  ...routines,
  ...eventWindow,
  isNativeUri,
  resolveSavedImage,
  toOriginalImageUrl,
  // Cuelga de `utils` para que los .vue lo consuman como `this.$utils.fetchBlobSafe(...)` (boot/utils.js
  // registra ESTE objeto como $utils) en vez de importar el archivo directo. El export nombrado del
  // final del barrel se conserva para los tests y para loadImageSafe, que no son .vue.
  fetchBlobSafe,
  addFilesToZip,
  // Visibilidad de grupos por logica condicionada. Se cuelga de $utils para consumo en .vue
  // (OccurrenceFillForm.vue / FormTestFill.vue) y ademas se exporta nombrado desde ./formVisibility.js para los
  // getters del store (no-.vue) y los tests.
  isItemVisibleByLogic,
  isAnswerGiven,
  processPhoto,
  // Mismo motivo que fetchBlobSafe: funciones puras que se llamaban con import directo desde
  // varios .vue. Registradas aqui, esos componentes las consumen como $utils.*.
  // Los modulos con estado propio (logTracker, networkTracker, fcm) NO se registran a proposito.
  organizationIdOf,
  normalizeAddonImageUrl,
  ...answerOptions,
  ...logicRules,
  ...tasks,
  ...visibilityGraph,
  buildCommentsToResync,
  // Admin de Analytics del super admin.
  ...analytics,
  isDirectGeneralPdfAllowed,
  saveFileToDevice,
  dataUrlToBlob (dataUrl) {
    const arr = dataUrl.split(',')
    const mime = /^[^:]*:([^;]*);/.exec(arr[0])[1]
    const bstr = atob(arr[1])
    let n = bstr.length
    const u8arr = new Uint8Array(n)
    while (n--) u8arr[n] = bstr.codePointAt(n)
    return new Blob([u8arr], { type: mime })
  },
  capitalize (str) {
    if (typeof str !== 'string' || str.length === 0) return str
    return str.charAt(0).toUpperCase() + str.slice(1)
  },
  randomStr (length) {
    let result = ''
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
    const charactersLength = characters.length
    for (let i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength))
    }
    return result
  },
  avgString (avg) {
    return avg === 0
      ? '00.00%'
      : `${avg.toFixed(2)}%`
  },
  formHasUserRoles (assignmentRoles, userRoles) {
    return assignmentRoles.filter(assignment_role => userRoles.includes(assignment_role)).length > 0
  },
  answersObjectToArray: function (object) {
    return _.map(object, (value, item_id) => {
      return { value, item_id: Number.parseInt(item_id) }
    })
  },
  sumArray,
  avgArray (arr) {
    return arr.length > 0 ? (arr.reduce((prev, curr) => prev + curr) / arr.length).toFixed(2) : 0
  },
  percentage (a, b) {
    return (a / b) * 100
  },
  sumKey,
  avgKey (collection, key) {
    const values = collection.map(el => el[key])

    return values.length > 0 ? values.reduce((prev, curr) => prev + curr) / values.length : 0
  },
  countWhereEqual (collection, key, value) {
    return collection.map(el => el[key]).filter(el => el === value).length
  },
  sumKeyWhereNotEqual,
  getForOptionList (array, labelKey, valueKey, object = true) {
    return object
      ? array.map(object => ({
        label: object[labelKey],
        value: object[valueKey],
        object: object,
        [valueKey]: object[valueKey],
        [labelKey]: object[labelKey]
      }))
      : array.map(object => ({
        label: object[labelKey],
        value: object[valueKey],
        [valueKey]: object[valueKey],
        [labelKey]: object[labelKey]
      }))
  },
  getItemOptions (item) {
    const raw = typeof item.options === 'object' ? item.options : JSON.parse(item.options)
    return item.type === 'form-table' || item.type === 'form-checkitem' || item.type === 'form-reading'
      ? raw
      : this.getForOptionList(raw, 'option', 'option')
  },
  getImageDataUrl: (url, callback) => {
    const xhr = new XMLHttpRequest()

    xhr.onload = () => {
      const reader = new FileReader()

      reader.onloadend = () => {
        callback(reader.result)
      }

      reader.readAsDataURL(xhr.response)
    }

    xhr.open('GET', url)
    xhr.responseType = 'blob'
    xhr.send()
  },
  getItemObtainedPoints,
  getItemPossiblePoints,
  getCemexScoreResult,
  getMifelScoreResult,
  GMapCircle (lat, lng, rad, detail = 8) {
    const uri = 'https://maps.googleapis.com/maps/api/staticmap?'
    let staticMapSrc = 'center=' + lat + ',' + lng
    staticMapSrc += '&path=fillcolor:0xB8353D33|color:0xB8353Dff|weight:2'

    const r = 6371

    const pi = Math.PI

    const _lat = (lat * pi) / 180
    const _lng = (lng * pi) / 180
    const d = (rad / 1000) / r

    let i = 0

    for (i = 0; i <= 360; i += detail) {
      const brng = i * pi / 180

      let pLat = Math.asin(Math.sin(_lat) * Math.cos(d) + Math.cos(_lat) * Math.sin(d) * Math.cos(brng))
      const pLng = ((_lng + Math.atan2(Math.sin(brng) * Math.sin(d) * Math.cos(_lat), Math.cos(d) - Math.sin(_lat) * Math.sin(pLat))) * 180) / pi
      pLat = (pLat * 180) / pi

      staticMapSrc += '|' + pLat + ',' + pLng
    }
    return uri + encodeURI(staticMapSrc)
  },
  getRandomColor () {
    const letters = '0123456789ABCDEF'.split('')
    let color = '#'
    for (let i = 0; i < 6; i++) {
      color += letters[Math.floor(Math.random() * 16)]
    }
    return color
  },
  downloadRaw (filename, data, mimeType) {
    let blob = new Blob([data], { type: mimeType })
    filename = `${filename.split('.')[0]}.${filename.split('.')[1]}`
    if (Capacitor.isNativePlatform()) {
      this.saveToDevice(filename, blob, mimeType)
    } else {
      this.saveToDesktop(filename, blob)
    }
  },
  getFileExtension (filename) {
    const clean = /^https?:\/\//i.test(filename) ? filename.split(/[?#]/)[0] : filename
    return clean.split('.').pop()
  },
  // dataURL completo (con prefijo data:image/...;base64,) para los casos que necesitan la imagen
  // como string estable: previews que sobreviven a la revocación del blob URL y payloads JSON.
  blobToDataUrl (blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result)
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
  },
  download (filename, data, mimeType) {
    const blob = new Blob([data], { type: mimeType })
    filename = `${filename.split('.')[0]} (${Date.now()}).${filename.split('.')[1]}`
    if (Capacitor.isNativePlatform()) {
      this.saveToDevice(filename, blob, mimeType)
    } else {
      this.saveToDesktop(filename, blob)
    }
  },

  saveToDesktop (filename, blob) {
    const urlCreator = globalThis.URL || globalThis.webkitURL
    const fileURL = urlCreator.createObjectURL(blob)
    const tag = document.createElement('a')
    tag.href = fileURL
    tag.download = filename
    document.body.appendChild(tag)
    tag.click()
    document.body.removeChild(tag)
  },

  async saveToDevice (filename, blob, mimeType) {
    const extension = this.getFileExtension(filename).toLowerCase()
    const mimeMap = {
      'pdf': 'application/pdf',
      'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'png': 'image/png',
      'webp': 'image/webp'
    }
    const blobType = mimeMap[extension] || mimeType || 'application/octet-stream'

    try {
      const { uri, opened } = await saveFileToDevice(filename, blob, blobType)
      this.showDownloadDialog(uri, !opened)
    } catch (err) {
      console.error('Error al guardar archivo en dispositivo:', err)
    }
  },

  showDownloadDialog (fileURL, showWarning = false) {
    let message = 'El archivo se generó correctamente y está listo para compartir.'
    if (showWarning) {
        message += '\n\nIMPORTANTE: Para visualizar el archivo, necesita una aplicación compatible instalada en su dispositivo.';
      }
    setTimeout(() => {
      Dialog.create({
        title: 'Archivo disponible',
        message: message,
        ok: {
          label: 'Compartir',
          flat: true
        },
        cancel: {
          label: 'Finalizar',
          flat: true
        }
      }).onOk(async () => {
        try {
          await Share.share({ files: [fileURL] })
        } catch (e) {
          console.error('Error al compartir archivo:', e)
        }
      })
    }, 500)
  },

  dataURItoBlob (dataURI) {
    const isBase64 = dataURI.split(',')[0].split(';')[1] === 'base64'
    let byteString

    if (isBase64) {
      // convert base64 to raw binary data held in a string
      // doesn't handle URLEncoded DataURIs - see SO answer #6850276 for code that does this
      byteString = atob(dataURI.split(',')[1])
    } else {
      byteString = dataURI.split(',')[1]
    } // separate out the mime component

    const mimeString = dataURI
      .split(',')[0]
      .split(':')[1]
      .split(';')[0] // write the bytes of the string to an ArrayBuffer

    const ab = new ArrayBuffer(byteString.length) // create a view into the buffer

    const ia = new Uint8Array(ab) // set the bytes of the buffer to the correct values

    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.codePointAt(i)
    } // write the ArrayBuffer to a blob, and you're done

    const blob = new Blob([ab], {
      type: mimeString
    })
    return blob
  },
  getMimeTypes (fileTypes) {
    const mimeTypes = []
    fileTypes.forEach(type => {
      switch (type) {
        case 'pdf':
          mimeTypes.push('application/pdf')
          break

        case 'excel':
          mimeTypes.push('application/vnd.ms-excel')
          mimeTypes.push('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')
          break

        case 'word':
          mimeTypes.push('application/msword')
          mimeTypes.push('application/vnd.openxmlformats-officedocument.wordprocessingml.document')
          break

        case 'powerpoint':
          mimeTypes.push('application/vnd.ms-powerpoint')
          mimeTypes.push('application/vnd.openxmlformats-officedocument.presentationml.presentation')
          break

        default:
          break
      }
    })
    return mimeTypes
  },
  getFileFromSystem (fileTypes, maxSize = 2) {
    const maxSizeInBytes = maxSize * 1e+6
    return new Promise((resolve, reject) => {
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = Platform.is.android ? '*/*' : this.getMimeTypes(fileTypes).join(',')
      input.hidden = true
      input.addEventListener('change', event => {
        const file = event.target.files[0]
        if (this.getMimeTypes(fileTypes).indexOf(file.type) === -1) {
          Dialog.create({
            title: 'Archivo no aceptado',
            message: `Sólo se aceptan archivos ${fileTypes.join(', ')}.`
          })
          reject(new Error('canceled'))
        } else {
          if (file.size > maxSizeInBytes) {
            Dialog.create({
              title: 'Tamaño no permitido',
              message: `Sólo se aceptan archivos con un peso máximo de ${maxSize} MB.`
            })
            reject(new Error('canceled'))
          }
          resolve(file)
        }
      })
      const offvisibility = () => {
        if (!document.hidden) {
          document.removeEventListener('visibilitychange', offvisibility)
          setTimeout(() => {
            if (input.files.length === 0) {
              reject(new Error('canceled'))
            }
          }, 100)
        }
      }
      document.addEventListener('visibilitychange', offvisibility, false)
      // Evento nativo `cancel` del <input type=file> (Chrome 113+/Safari 16.4+): en DESKTOP el
      // diálogo de archivos NO cambia la visibilidad de la página, así que `visibilitychange` nunca
      // rechazaba y los guards anti-doble-tap (loading=true) dejaban el spinner pegado hasta recargar
      // en los 3 flujos de evidencia. `cancel` lo cubre; visibilitychange queda de fallback. [review Hugo]
      input.addEventListener('cancel', () => {
        document.removeEventListener('visibilitychange', offvisibility)
        reject(new Error('canceled'))
      })
      input.click()
    })
  },
  dateISOtoCalendar (isoString) {
    let object = new Date(isoString.replaceAll('-', '/'))
    return date.formatDate(object, 'DD/MM/YYYY')
  },
  moneyFormat (number, moneyPrefix = '$', separator = ',') {
    if (Number.isNaN(Number(number)) || number === null) {
      console.error('UTILS: Data provided to moneyFormat is not a Number, provided:', typeof number, number)
      number = 0
    }
    const money = Number.parseFloat(number).toFixed(2).split('.')
    // Separador de miles por cada tramo de digitos, sin lookahead (el regex anterior tenia backtracking
    // super-lineal). Mismo resultado que /(\d)(?=(\d{3})+(?!\d))/g, incluido '-1234' o '1e+21'.
    money[0] = money[0].replaceAll(/\d+/g, (digits) => {
      let grouped = digits.slice(0, digits.length % 3 || 3)
      for (let i = grouped.length; i < digits.length; i += 3) grouped += separator + digits.slice(i, i + 3)
      return grouped
    })
    return moneyPrefix + money.join('.')
  },
  versionCompare (v1, v2, options) {
    const lexicographical = options && options.lexicographical
    const zeroExtend = options && options.zeroExtend
    let v1parts = v1.split('.')
    let v2parts = v2.split('.')

    function isValidPart (x) {
      return (lexicographical ? /^\d+[A-Za-z]*$/ : /^\d+$/).test(x)
    }

    if (!v1parts.every(isValidPart) || !v2parts.every(isValidPart)) {
      return NaN
    }

    if (zeroExtend) {
      while (v1parts.length < v2parts.length) v1parts.push('0')
      while (v2parts.length < v1parts.length) v2parts.push('0')
    }

    if (!lexicographical) {
      v1parts = v1parts.map(Number)
      v2parts = v2parts.map(Number)
    }

    for (let i = 0; i < v1parts.length; ++i) {
      if (v2parts.length === i) {
        return 1
      }

      if (v1parts[i] === v2parts[i]) {
        continue
      } else if (v1parts[i] > v2parts[i]) {
        return 1
      } else {
        return -1
      }
    }

    if (v1parts.length !== v2parts.length) {
      return -1
    }

    return 0
  },
  freezeElementScrolling (freeze, elementId) {
    if (!document.getElementById(elementId)) {
      console.error('Element to freeze does not exist.')
      return
    }
    if (freeze === true) {
      document.body.addEventListener('touchmove', freezeVp, false)
      document.getElementById(elementId).addEventListener('touchstart', freezeVp, false)
      document.getElementById(elementId).addEventListener('touchmove', freezeVp, false)
    } else {
      document.body.removeEventListener('touchmove', freezeVp, false)
      document.getElementById(elementId).removeEventListener('touchstart', freezeVp, false)
      document.getElementById(elementId).removeEventListener('touchmove', freezeVp, false)
    }
  },
  DDMMYYYYHHmm: date => moment(date).format('DD/MM/YYYY HH:mm'),
  DDMMYYYY: date => date ? moment(date).format('DD/MM/YYYY') : '-',
  // "hace 5 minutos" / "5 minutes ago" -- boot/moment.js ya fija el locale global en 'es', asi que
  // no hace falta pasarlo aqui. Usada por LicenseGlobalKpiCounter (TKT-2285 fase 3) para mostrar
  // el `cached_at` del KPI global.
  relativeTime: date => date ? moment(date).fromNow() : '-',
  getAnswerColor (value) {
    const normalizedValue = value.toLowerCase()
    const colors = {
      red: { r: 182, g: 5, b: 55 },
      green: { r: 0, g: 204, b: 58 },
      gray: { r: 96, g: 96, b: 96 }
    }

    if (normalizedValue.includes('no') || normalizedValue.includes('incum')) {
      return `rgba(${colors.red.r}, ${colors.red.g}, ${colors.red.b}, 1)`
    }

    if (normalizedValue === '"na"' || normalizedValue === 'na') {
      return `rgba(${colors.gray.r}, ${colors.gray.g}, ${colors.gray.b}, 1)`
    }

    return `rgba(${colors.green.r}, ${colors.green.g}, ${colors.green.b}, 1)`
  }
}

function freezeVp (e) {
  e.preventDefault()
}

export function val (val) {
  return val
}

export function notVal (val) {
  return !val
}

export function naVal (val) {
  val = val[0] === '"' ? JSON.parse(val) : val
  return typeof val === 'string' && val.toLowerCase() === 'na'
}
export function zeroVal (val) {
  return val === 0
}

export function positiveVal (val) {
  return val > 0
}

export function criticalPoint (item) {
  return item.priority === 'critical_point'
}

export function notCriticalPoint (item) {
  return item.priority !== 'critical_point'
}

export default utils

export { fetchBlobSafe } from './fetchBlobSafe'
